import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../../../auth';
import { authHeaders, jsonAuthHeaders } from '../../../shared/authHeaders';

type AnnotationCategory = 'copy' | 'design';
type AnnotationStatus = 'open' | 'converted' | 'needs-review' | 'resolved' | 'dismissed';
type SurfaceStatus = 'pending' | 'reviewed' | 'needs-fix' | 'approved';

type VisibleTextBlock = {
  kind: string;
  text: string;
  selector: string | null;
};

type DesignReviewCapture = {
  captureId: string;
  reviewSurfaceId: string;
  capturedAt: string | null;
  verify: string | null;
  visibleText: VisibleTextBlock[];
  image: {
    fileName: string;
    path: string;
    contentType: string | null;
    sizeBytes: number;
    width: number | null;
    height: number | null;
  };
};

type DesignReviewAnnotation = {
  id: string;
  reviewSurfaceId: string;
  category: AnnotationCategory;
  status: AnnotationStatus;
  currentCopy: string | null;
  proposedCopy: string | null;
  notes: string | null;
  linkedAdminPlanId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type DesignReviewSurface = {
  reviewSurfaceId: string;
  surfaceKind: string | null;
  route: string | null;
  role: string | null;
  viewport: string | null;
  state: string | null;
  pageUrl: string | null;
  emailTemplateId: string | null;
  sourceLabel: string | null;
  emailSubject: string | null;
  emailTextBody: string | null;
  status: SurfaceStatus;
  lastReviewedAt: string | null;
  latestScreenshotCapturedAt: string | null;
  stale: boolean;
  captures: DesignReviewCapture[];
  latestCapture: DesignReviewCapture | null;
  visibleText: VisibleTextBlock[];
  annotations: DesignReviewAnnotation[];
};

type DesignReviewSnapshot = {
  generatedAt: string;
  report: {
    generatedAt: string | null;
    path: string;
    summary: {
      surfaceCount: number;
      captureCount: number;
      appScreenCount: number;
      emailCount: number;
      errorCount: number;
    } | null;
    errors: string[];
  };
  surfaces: DesignReviewSurface[];
};

export type DesignReviewFilters = {
  surfaceKind: 'all' | string;
  role: 'all' | string;
  viewport: 'all' | string;
  page: string;
  stale: 'all' | 'stale' | 'fresh';
  review: 'all' | 'needs-review' | 'reviewed';
  query: string;
};

type AnnotationDraft = {
  text: string;
};

const FILTER_STORAGE_KEY = 'drawclub.admin.designReviewFilters';

const DEFAULT_FILTERS: DesignReviewFilters = {
  surfaceKind: 'all',
  role: 'all',
  viewport: 'all',
  page: '',
  stale: 'all',
  review: 'all',
  query: '',
};

const DEFAULT_ANNOTATION_DRAFT: AnnotationDraft = {
  text: '',
};

export function DesignReviewPage() {
  const { getToken } = useAuth();
  const [snapshot, setSnapshot] = useState<DesignReviewSnapshot | null>(null);
  const [filters, setFilters] = useState<DesignReviewFilters>(() => loadFilters());
  const [selectedSurfaceId, setSelectedSurfaceId] = useState<string | null>(null);
  const [selectedCaptureId, setSelectedCaptureId] = useState<string | null>(null);
  const [annotationDraft, setAnnotationDraft] = useState<AnnotationDraft>(DEFAULT_ANNOTATION_DRAFT);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadSnapshot = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/admin/design-review', {
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json() as DesignReviewSnapshot;
      setSnapshot(data);
      setSelectedSurfaceId((current) => current && data.surfaces.some(surface => surface.reviewSurfaceId === current)
        ? current
        : data.surfaces[0]?.reviewSurfaceId ?? null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Design review could not load.');
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  useEffect(() => {
    window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const surfaces = snapshot?.surfaces ?? [];
  const filteredSurfaces = useMemo(() => filterDesignReviewSurfaces(surfaces, filters), [surfaces, filters]);
  const selectedSurface = filteredSurfaces.find(surface => surface.reviewSurfaceId === selectedSurfaceId)
    ?? filteredSurfaces[0]
    ?? null;
  const selectedCapture = selectedSurface?.captures.find(capture => capture.captureId === selectedCaptureId)
    ?? selectedSurface?.latestCapture
    ?? selectedSurface?.captures[0]
    ?? null;

  useEffect(() => {
    if (!selectedSurface) return;
    setSelectedCaptureId((current) => selectedSurface.captures.some(capture => capture.captureId === current)
      ? current
      : selectedSurface.latestCapture?.captureId ?? selectedSurface.captures[0]?.captureId ?? null);
  }, [selectedSurface?.reviewSurfaceId]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      if (!['ArrowDown', 'ArrowUp', 'j', 'k'].includes(event.key)) return;
      event.preventDefault();
      const currentIndex = selectedSurface
        ? filteredSurfaces.findIndex(surface => surface.reviewSurfaceId === selectedSurface.reviewSurfaceId)
        : -1;
      const nextIndex = event.key === 'ArrowDown' || event.key === 'j'
        ? Math.min(filteredSurfaces.length - 1, currentIndex + 1)
        : Math.max(0, currentIndex - 1);
      const nextSurface = filteredSurfaces[nextIndex];
      if (nextSurface) setSelectedSurfaceId(nextSurface.reviewSurfaceId);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredSurfaces, selectedSurface]);

  const optionValues = useMemo(() => getFilterOptions(surfaces), [surfaces]);

  async function saveAnnotation() {
    if (!selectedSurface) return;
    setIsSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/design-review/surfaces/${encodeURIComponent(selectedSurface.reviewSurfaceId)}/annotations`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          category: 'design',
          notes: annotationDraft.text || null,
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      setAnnotationDraft(DEFAULT_ANNOTATION_DRAFT);
      await loadSnapshot();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Annotation could not be saved.');
    } finally {
      setIsSaving(false);
    }
  }

  async function createPlan(annotationId: string) {
    setIsSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/design-review/annotations/${annotationId}/plan`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(await response.text());
      await loadSnapshot();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Plan could not be created.');
    } finally {
      setIsSaving(false);
    }
  }

  async function updateAnnotationStatus(annotationId: string, status: AnnotationStatus) {
    setIsSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/design-review/annotations/${annotationId}`, {
        method: 'PATCH',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error(await response.text());
      await loadSnapshot();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Change request status could not be saved.');
    } finally {
      setIsSaving(false);
    }
  }

  async function updateSurfaceReviewStatus(surface: DesignReviewSurface, reviewed: boolean) {
    setIsSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/design-review/surfaces/${encodeURIComponent(surface.reviewSurfaceId)}`, {
        method: 'PATCH',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ status: reviewed ? 'reviewed' : 'pending' }),
      });
      if (!response.ok) throw new Error(await response.text());
      await loadSnapshot();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Review state could not be saved.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="min-h-full bg-bg">
      <header className="border-b border-line bg-panel px-6 py-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-text">Design Review</h1>
            <p className="mt-1 text-sm text-text-dim">
              Review screenshots, email previews, visible copy, and design notes before promoting work into plans.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadSnapshot()}
            className="rounded-lg border border-line bg-bg px-4 py-2 text-sm font-medium text-text transition-colors hover:border-text/40"
          >
            Refresh
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs text-text-dim">
          <Pill>{snapshot?.report.summary?.surfaceCount ?? 0} surfaces</Pill>
          <Pill>{snapshot?.report.summary?.captureCount ?? 0} screenshots</Pill>
          <Pill>{snapshot?.report.summary?.emailCount ?? 0} emails</Pill>
          <Pill>Report: {snapshot?.report.generatedAt ? formatDate(snapshot.report.generatedAt) : 'not generated yet'}</Pill>
        </div>
        {snapshot?.report.errors?.length ? (
          <div className="mt-3 rounded-lg border border-warn/25 bg-warn/10 p-3 text-sm text-text">
            {snapshot.report.errors[0]}
          </div>
        ) : null}
      </header>

      <main className="grid min-h-[calc(100vh-7rem)] grid-cols-1 gap-0 lg:grid-cols-[320px_minmax(0,1fr)_380px]">
        <aside className="border-b border-line bg-panel p-4 lg:border-b-0 lg:border-r">
          <DesignReviewFiltersPanel
            filters={filters}
            options={optionValues}
            onChange={setFilters}
          />
          <div className="mt-4 text-xs text-text-faint">{filteredSurfaces.length} of {surfaces.length} surfaces</div>
          <div className="mt-3 max-h-[65vh] space-y-2 overflow-auto pr-1">
            {filteredSurfaces.map((surface) => (
              <button
                key={surface.reviewSurfaceId}
                type="button"
                onClick={() => setSelectedSurfaceId(surface.reviewSurfaceId)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  selectedSurface?.reviewSurfaceId === surface.reviewSurfaceId
                    ? 'border-accent bg-accent/10'
                    : 'border-line bg-bg hover:border-text/30'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 truncate text-sm font-semibold text-text">
                    {formatSurfaceTitle(surface)}
                  </div>
                </div>
                <div className="mt-1 truncate text-xs text-text-dim">{surface.reviewSurfaceId}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {surface.surfaceKind && <TinyTag>{surface.surfaceKind}</TinyTag>}
                  {surface.role && <TinyTag>{surface.role}</TinyTag>}
                  {surface.viewport && <TinyTag>{surface.viewport}</TinyTag>}
                  {surface.stale && <TinyTag tone="warn">stale</TinyTag>}
                  {isSurfaceReviewed(surface) ? <TinyTag tone="ok">reviewed</TinyTag> : <TinyTag>needs review</TinyTag>}
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className="min-w-0 p-5">
          {isLoading ? (
            <EmptyPanel title="Loading design review" body="Collecting screenshots and annotations." />
          ) : error ? (
            <EmptyPanel title="Design review could not load" body={error} />
          ) : selectedSurface ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-2xl text-text">{formatSurfaceTitle(selectedSurface)}</h2>
                  <p className="mt-1 break-all text-sm text-text-dim">{selectedSurface.reviewSurfaceId}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedSurface.pageUrl && (
                    <a
                      href={selectedSurface.pageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border border-line bg-panel px-3 py-2 text-sm font-medium text-text transition-colors hover:border-text/40"
                    >
                      Open page
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => void updateSurfaceReviewStatus(selectedSurface, !isSurfaceReviewed(selectedSurface))}
                    disabled={isSaving}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                      isSurfaceReviewed(selectedSurface)
                        ? 'border-line bg-panel text-text hover:border-text/40'
                        : 'border-accent bg-accent/10 text-accent hover:border-accent/70'
                    }`}
                  >
                    {isSurfaceReviewed(selectedSurface) ? 'Mark unreviewed' : 'Mark reviewed'}
                  </button>
                  {selectedCapture && (
                    <button
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      className="rounded-lg border border-line bg-panel px-3 py-2 text-sm font-medium text-text transition-colors hover:border-text/40"
                    >
                      Lightbox
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-line bg-panel p-3">
                {selectedCapture ? (
                  <>
                    <div className="max-h-[70vh] overflow-auto rounded border border-line bg-black">
                      <SecureScreenshot
                        captureId={selectedCapture.captureId}
                        alt={`Screenshot for ${formatSurfaceTitle(selectedSurface)}`}
                        getToken={getToken}
                        className="block h-auto w-auto max-h-none max-w-none"
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-text-dim">
                      <span>{selectedCapture.image.fileName}</span>
                      <span>{selectedCapture.image.width ?? '?'} x {selectedCapture.image.height ?? '?'} · {formatBytes(selectedCapture.image.sizeBytes)}</span>
                    </div>
                  </>
                ) : (
                  <EmptyPanel title="No screenshot captured" body="Run npm run test:design-review --workspace @modelcast/e2e to collect screenshots." />
                )}
              </div>

              {selectedSurface.captures.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {selectedSurface.captures.map((capture, index) => (
                    <button
                      key={capture.captureId}
                      type="button"
                      onClick={() => setSelectedCaptureId(capture.captureId)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        selectedCapture?.captureId === capture.captureId
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-line bg-panel text-text-dim hover:text-text'
                      }`}
                    >
                      Capture {index + 1}
                    </button>
                  ))}
                </div>
              )}

              <VisibleTextPanel
                visibleText={selectedCapture?.visibleText ?? selectedSurface.visibleText}
                onSelect={(text) => setAnnotationDraft((draft) => ({
                  text: draft.text.trim()
                    ? `${draft.text.trim()}\n\nCopy/context:\n${text}`
                    : `Copy/context:\n${text}`,
                }))}
              />
            </div>
          ) : (
            <EmptyPanel title="No surfaces match" body="Adjust the filters or run the manual design-review screenshot sweep." />
          )}
        </section>

        <aside className="border-t border-line bg-panel p-4 lg:border-l lg:border-t-0">
          {selectedSurface ? (
            <div className="space-y-5">
              {selectedSurface.surfaceKind === 'email' && (
                <section className="rounded-lg border border-line bg-bg p-3">
                  <h3 className="text-sm font-semibold text-text">Email</h3>
                  <dl className="mt-2 space-y-2 text-xs">
                    <div><dt className="text-text-faint">Template ID</dt><dd className="text-text">{selectedSurface.emailTemplateId ?? 'Unknown'}</dd></div>
                    <div><dt className="text-text-faint">Subject</dt><dd className="text-text">{selectedSurface.emailSubject ?? 'No subject captured'}</dd></div>
                  </dl>
                  {selectedSurface.emailTextBody && (
                    <pre className="mt-3 max-h-52 overflow-auto whitespace-pre-wrap rounded border border-line bg-panel p-2 text-xs leading-5 text-text-dim">
                      {selectedSurface.emailTextBody}
                    </pre>
                  )}
                </section>
              )}

              <section>
                <h3 className="text-sm font-semibold text-text">Add change request</h3>
                <textarea
                  value={annotationDraft.text}
                  onChange={(event) => setAnnotationDraft({ text: event.target.value })}
                  rows={7}
                  className="mt-3 w-full rounded-lg border border-line bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent"
                  placeholder="Describe the change, question, or review note"
                />
                <button
                  type="button"
                  onClick={() => void saveAnnotation()}
                  disabled={isSaving || !annotationDraft.text.trim()}
                  className="mt-3 w-full rounded-lg bg-text px-4 py-2 text-sm font-semibold text-bg transition-opacity disabled:opacity-45"
                >
                  Save change request
                </button>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-text">Change requests</h3>
                <div className="mt-3 space-y-3">
                  {selectedSurface.annotations.length > 0 ? selectedSurface.annotations.map((annotation) => (
                    <article key={annotation.id} className="rounded-lg border border-line bg-bg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-wrap gap-1">
                          <TinyTag tone={annotation.status === 'needs-review' ? 'warn' : annotation.status === 'resolved' ? 'ok' : 'neutral'}>
                            {labelize(annotation.status)}
                          </TinyTag>
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                          {annotation.linkedAdminPlanId ? (
                            <a href="/admin/plans" className="text-xs font-semibold text-accent hover:underline">Plan created</a>
                          ) : annotation.status === 'open' ? (
                            <button
                              type="button"
                              onClick={() => void createPlan(annotation.id)}
                              disabled={isSaving}
                              className="text-xs font-semibold text-accent hover:underline disabled:opacity-45"
                            >
                              Create plan
                            </button>
                          ) : null}
                          {(annotation.status === 'open' || annotation.status === 'converted') && (
                            <button
                              type="button"
                              onClick={() => void updateAnnotationStatus(annotation.id, 'resolved')}
                              disabled={isSaving}
                              className="text-xs font-semibold text-accent hover:underline disabled:opacity-45"
                            >
                              Mark resolved
                            </button>
                          )}
                          {annotation.status === 'needs-review' && (
                            <button
                              type="button"
                              onClick={() => void updateAnnotationStatus(annotation.id, 'resolved')}
                              disabled={isSaving}
                              className="text-xs font-semibold text-accent hover:underline disabled:opacity-45"
                            >
                              Mark resolved
                            </button>
                          )}
                          {(annotation.status === 'resolved' || annotation.status === 'dismissed') && (
                            <button
                              type="button"
                              onClick={() => void updateAnnotationStatus(annotation.id, 'open')}
                              disabled={isSaving}
                              className="text-xs font-semibold text-accent hover:underline disabled:opacity-45"
                            >
                              Reopen
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 whitespace-pre-wrap text-sm leading-5 text-text">
                        {formatAnnotationText(annotation)}
                      </div>
                    </article>
                  )) : (
                    <div className="rounded-lg border border-dashed border-line p-4 text-sm text-text-dim">
                      No change requests yet.
                    </div>
                  )}
                </div>
              </section>
            </div>
          ) : null}
        </aside>
      </main>

      {lightboxOpen && selectedCapture && selectedSurface && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[80] flex flex-col bg-black/90 p-4 text-white"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <div className="text-lg font-semibold">{formatSurfaceTitle(selectedSurface)}</div>
              <div className="mt-1 text-sm text-white/60">{selectedCapture.image.fileName}</div>
            </div>
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="rounded-lg border border-white/20 px-3 py-2 text-sm font-semibold text-white"
            >
              Close
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-white/10 bg-black p-2" onClick={(event) => event.stopPropagation()}>
            <SecureScreenshot
              captureId={selectedCapture.captureId}
              alt={`Screenshot for ${formatSurfaceTitle(selectedSurface)}`}
              getToken={getToken}
              className="block h-auto w-auto max-h-none max-w-none"
            />
          </div>
          {selectedSurface.pageUrl && (
            <a href={selectedSurface.pageUrl} target="_blank" rel="noreferrer" className="mt-3 break-all text-sm text-white/75 hover:text-white">
              Open page: {selectedSurface.pageUrl}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function DesignReviewFiltersPanel({
  filters,
  options,
  onChange,
}: {
  filters: DesignReviewFilters;
  options: {
    surfaceKinds: string[];
    roles: string[];
    viewports: string[];
    pages: string[];
  };
  onChange: (filters: DesignReviewFilters) => void;
}) {
  function patch(next: Partial<DesignReviewFilters>) {
    onChange({ ...filters, ...next });
  }

  return (
    <div className="space-y-3">
      <input
        value={filters.query}
        onChange={(event) => patch({ query: event.target.value })}
        className="w-full rounded-lg border border-line bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent"
        placeholder="Search surfaces and copy"
      />
      <div className="grid grid-cols-2 gap-2">
        <FilterSelect label="Kind" value={filters.surfaceKind} values={['all', ...options.surfaceKinds]} onChange={(surfaceKind) => patch({ surfaceKind })} />
        <FilterSelect label="Role" value={filters.role} values={['all', ...options.roles]} onChange={(role) => patch({ role })} />
        <FilterSelect label="Viewport" value={filters.viewport} values={['all', ...options.viewports]} onChange={(viewport) => patch({ viewport })} />
        <FilterSelect label="Review" value={filters.review} values={['all', 'needs-review', 'reviewed']} onChange={(review) => patch({ review: review as DesignReviewFilters['review'] })} />
        <FilterSelect label="Stale" value={filters.stale} values={['all', 'stale', 'fresh']} onChange={(stale) => patch({ stale: stale as DesignReviewFilters['stale'] })} />
        <FilterSelect label="Page" value={filters.page} values={['', ...options.pages]} onChange={(page) => patch({ page })} />
      </div>
      <button
        type="button"
        onClick={() => onChange(DEFAULT_FILTERS)}
        className="text-xs font-semibold text-text-dim hover:text-text"
      >
        Clear filters
      </button>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-text-faint">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-lg border border-line bg-bg px-2 py-2 text-sm normal-case tracking-normal text-text"
      >
        {values.map((option) => (
          <option key={option || 'all-pages'} value={option}>{option || 'all'}</option>
        ))}
      </select>
    </label>
  );
}

function SecureScreenshot({
  captureId,
  alt,
  getToken,
  className,
}: {
  captureId: string;
  alt: string;
  getToken: () => Promise<string | null>;
  className?: string;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    let nextUrl: string | null = null;

    async function loadImage() {
      setError(null);
      setObjectUrl(null);
      try {
        const token = await getToken();
        const response = await fetch(`/api/admin/design-review/media/${captureId}`, {
          headers: authHeaders(token),
        });
        if (!response.ok) throw new Error('Screenshot could not be loaded.');
        const blob = await response.blob();
        nextUrl = URL.createObjectURL(blob);
        if (!isCancelled) {
          setObjectUrl(nextUrl);
        } else {
          URL.revokeObjectURL(nextUrl);
          nextUrl = null;
        }
      } catch (loadError) {
        if (!isCancelled) setError(loadError instanceof Error ? loadError.message : 'Screenshot could not be loaded.');
      }
    }

    void loadImage();
    return () => {
      isCancelled = true;
      if (nextUrl) URL.revokeObjectURL(nextUrl);
    };
  }, [captureId, getToken]);

  if (error) {
    return <div className="rounded-lg border border-line bg-bg p-6 text-center text-sm text-text-dim">{error}</div>;
  }

  if (!objectUrl) {
    return <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-line bg-bg text-sm text-text-dim">Loading screenshot</div>;
  }

  return <img src={objectUrl} alt={alt} className={className} />;
}

function VisibleTextPanel({
  visibleText,
  onSelect,
}: {
  visibleText: VisibleTextBlock[];
  onSelect: (text: string) => void;
}) {
  if (visibleText.length === 0) {
    return <EmptyPanel title="No visible copy snapshot" body="This capture did not record page text." />;
  }

  return (
    <section className="rounded-lg border border-line bg-panel p-4">
      <h3 className="text-sm font-semibold text-text">Visible copy</h3>
      <div className="mt-3 max-h-80 space-y-2 overflow-auto">
        {visibleText.map((block, index) => (
          <button
            key={`${block.kind}-${index}-${block.text}`}
            type="button"
            onClick={() => onSelect(block.text)}
            className="w-full rounded border border-line bg-bg p-2 text-left transition-colors hover:border-text/30"
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-text-faint">{block.kind}</div>
            <div className="mt-1 text-sm leading-5 text-text">{block.text}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

export function filterDesignReviewSurfaces(
  surfaces: DesignReviewSurface[],
  filters: DesignReviewFilters,
): DesignReviewSurface[] {
  const query = filters.query.trim().toLowerCase();
  return surfaces.filter((surface) => {
    if (filters.surfaceKind !== 'all' && surface.surfaceKind !== filters.surfaceKind) return false;
    if (filters.role !== 'all' && surface.role !== filters.role) return false;
    if (filters.viewport !== 'all' && surface.viewport !== filters.viewport) return false;
    if (filters.page && surface.route !== filters.page) return false;
    if (filters.stale === 'stale' && !surface.stale) return false;
    if (filters.stale === 'fresh' && surface.stale) return false;
    if (filters.review === 'reviewed' && !isSurfaceReviewed(surface)) return false;
    if (filters.review === 'needs-review' && isSurfaceReviewed(surface)) return false;
    if (!query) return true;
    return [
      surface.reviewSurfaceId,
      surface.sourceLabel,
      surface.route,
      surface.role,
      surface.viewport,
      surface.state,
      surface.emailSubject,
      surface.emailTextBody,
      ...surface.visibleText.map(block => block.text),
      ...surface.annotations.flatMap(annotation => [annotation.currentCopy, annotation.proposedCopy, annotation.notes]),
    ].some(value => value?.toLowerCase().includes(query));
  });
}

export function normalizeDesignReviewFilters(input: unknown): DesignReviewFilters {
  if (!input || typeof input !== 'object') return DEFAULT_FILTERS;
  const raw = input as Partial<Record<keyof DesignReviewFilters, unknown>>;
  const next: DesignReviewFilters = { ...DEFAULT_FILTERS };

  if (typeof raw.surfaceKind === 'string') next.surfaceKind = raw.surfaceKind || 'all';
  if (typeof raw.role === 'string') next.role = raw.role || 'all';
  if (typeof raw.viewport === 'string') next.viewport = raw.viewport || 'all';
  if (typeof raw.page === 'string') next.page = raw.page;
  if (isFilterValue(raw.stale, ['all', 'stale', 'fresh'])) next.stale = raw.stale;
  if (isFilterValue(raw.review, ['all', 'needs-review', 'reviewed'])) next.review = raw.review;
  if (typeof raw.query === 'string') next.query = raw.query;
  return next;
}

export function formatDesignReviewPlanDraft(surface: DesignReviewSurface, annotation: DesignReviewAnnotation): string {
  return [
    `Surface: ${surface.reviewSurfaceId}`,
    surface.route ? `Route: ${surface.route}` : null,
    surface.pageUrl ? `Page URL: ${surface.pageUrl}` : null,
    annotation.notes ? `Request:\n${annotation.notes}` : null,
    annotation.currentCopy ? `Current copy/context:\n${annotation.currentCopy}` : null,
    annotation.proposedCopy ? `Proposed copy:\n${annotation.proposedCopy}` : null,
  ].filter(Boolean).join('\n\n');
}

function getFilterOptions(surfaces: DesignReviewSurface[]) {
  const valueSet = (selector: (surface: DesignReviewSurface) => string | null) => (
    Array.from(new Set(surfaces.map(selector).filter(Boolean) as string[])).sort()
  );
  return {
    surfaceKinds: valueSet(surface => surface.surfaceKind),
    roles: valueSet(surface => surface.role),
    viewports: valueSet(surface => surface.viewport),
    pages: valueSet(surface => surface.route),
  };
}

function loadFilters(): DesignReviewFilters {
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    return normalizeDesignReviewFilters(JSON.parse(raw));
  } catch {
    return DEFAULT_FILTERS;
  }
}

function isFilterValue<T extends string>(value: unknown, options: readonly T[]): value is T {
  return typeof value === 'string' && options.includes(value as T);
}

function formatSurfaceTitle(surface: DesignReviewSurface) {
  return surface.sourceLabel || surface.route || surface.emailTemplateId || surface.reviewSurfaceId;
}

function formatAnnotationText(annotation: DesignReviewAnnotation) {
  if (annotation.notes) return annotation.notes;
  return [
    annotation.currentCopy ? `Current copy/context:\n${annotation.currentCopy}` : null,
    annotation.proposedCopy ? `Proposed copy:\n${annotation.proposedCopy}` : null,
  ].filter(Boolean).join('\n\n') || 'No request text captured.';
}

function isSurfaceReviewed(surface: DesignReviewSurface) {
  return (surface.status === 'reviewed' || surface.status === 'approved') && !surface.stale;
}

function labelize(value: string) {
  return value.replaceAll('-', ' ').replaceAll('_', ' ');
}

function TinyTag({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'warn' | 'ok' }) {
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
      tone === 'warn'
        ? 'border-warn/30 bg-warn/10 text-warn'
        : tone === 'ok'
          ? 'border-ok/30 bg-ok/10 text-ok'
          : 'border-line bg-panel text-text-dim'
    }`}>
      {children}
    </span>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return <span className="rounded-full border border-line bg-bg px-3 py-1">{children}</span>;
}

function EmptyPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-panel p-8 text-center">
      <h2 className="font-serif text-2xl text-text">{title}</h2>
      <p className="mt-2 text-sm text-text-dim">{body}</p>
    </div>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(value: string) {
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
